import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Wordmark } from "@/components/Wordmark";
import { brandFromLocation, brandMarkSrc } from "@/lib/brand";

type View = "login" | "forgot" | "sent";

const inputClass =
  "w-full rounded-[14px] border-[1.5px] border-ink-100 px-[14px] py-[13px] text-[15px] text-ink-900 bg-white outline-none transition focus:border-moss placeholder:text-ink-400";

function Logo(): React.JSX.Element {
  // No client to ask yet, so the hostname decides. From the moment the user is
  // authenticated the CLIENT decides instead (`useClientBranding`), which is what
  // makes arriving on the other brand's domain harmless rather than an error.
  const brand = brandFromLocation();
  return (
    <div className="mb-8 text-center">
      <div className="inline-flex items-center gap-2">
        <img src={brandMarkSrc(brand)} alt="" className="h-8 w-8 flex-shrink-0" />
        <Wordmark brand={brand} className="text-[22px]" />
      </div>
      <p className="mt-1 text-[12px] text-ink-400">CRM de Admisiones</p>
    </div>
  );
}

export function Login(): React.JSX.Element {
  const navigate = useNavigate();
  const [view, setView] = useState<View>("login");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setLoginError("Credenciales incorrectas. Verifica tu email y contraseña.");
      setLoginLoading(false);
      return;
    }

    navigate("/", { replace: true });
  }

  async function handleForgot(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });

    if (error) {
      setResetError("No pudimos enviar el correo. Intenta de nuevo.");
      setResetLoading(false);
      return;
    }

    setView("sent");
  }

  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center bg-ink-050 px-4 pb-12 overflow-y-auto">
      <Logo />

      <div className="w-full max-w-sm rounded-[14px] bg-white p-6 shadow-sm border border-ink-100">
        {view === "login" && (
          <>
            <h1 className="font-display text-[22px] font-bold text-ink-900 mb-1">
              Iniciar sesión
            </h1>
            <p className="mb-6 text-[13px] text-ink-500">
              Ingresa tus credenciales para continuar
            </p>

            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              <div className="flex flex-col gap-[6px]">
                <label htmlFor="email" className="text-[12px] font-semibold text-ink-700">
                  Correo electrónico
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@viadiseno.mx"
                  required
                  autoComplete="email"
                  autoFocus
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-[6px]">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-[12px] font-semibold text-ink-700">
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={() => { setResetEmail(email); setView("forgot"); }}
                    className="text-[12px] text-moss hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className={inputClass}
                />
              </div>

              {loginError !== null && (
                <div className="text-[12px] text-score-hot-fg bg-score-hot-bg px-3 py-2.5 rounded-[6px] leading-snug">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full py-[14px] bg-lime text-moss rounded-[14px] text-[15px] font-semibold mt-1 disabled:opacity-60 transition-opacity cursor-pointer"
              >
                {loginLoading ? "Iniciando sesión…" : "Entrar"}
              </button>
            </form>
          </>
        )}

        {view === "forgot" && (
          <>
            <h1 className="font-display text-[22px] font-bold text-ink-900 mb-1">
              Recuperar contraseña
            </h1>
            <p className="mb-6 text-[13px] text-ink-500">
              Te enviaremos un enlace para restablecer tu contraseña.
            </p>

            <form onSubmit={handleForgot} className="flex flex-col gap-4">
              <div className="flex flex-col gap-[6px]">
                <label htmlFor="reset-email" className="text-[12px] font-semibold text-ink-700">
                  Correo electrónico
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="admin@viadiseno.mx"
                  required
                  autoFocus
                  className={inputClass}
                />
              </div>

              {resetError !== null && (
                <div className="text-[12px] text-score-hot-fg bg-score-hot-bg px-3 py-2.5 rounded-[6px] leading-snug">
                  {resetError}
                </div>
              )}

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full py-[14px] bg-lime text-moss rounded-[14px] text-[15px] font-semibold mt-1 disabled:opacity-60 transition-opacity cursor-pointer"
              >
                {resetLoading ? "Enviando…" : "Enviar enlace"}
              </button>

              <button
                type="button"
                onClick={() => setView("login")}
                className="text-[13px] text-ink-500 hover:text-ink-900 text-center transition-colors"
              >
                Volver al inicio de sesión
              </button>
            </form>
          </>
        )}

        {view === "sent" && (
          <>
            <h1 className="font-display text-[22px] font-bold text-ink-900 mb-1">
              Revisa tu correo
            </h1>
            <p className="mb-6 text-[13px] text-ink-500">
              Enviamos un enlace de recuperación a{" "}
              <span className="font-medium text-ink-700">{resetEmail}</span>. Puede tardar
              unos minutos.
            </p>
            <button
              type="button"
              onClick={() => setView("login")}
              className="w-full py-[14px] border border-ink-100 text-ink-700 rounded-[14px] text-[15px] font-semibold transition-colors hover:bg-ink-050 cursor-pointer"
            >
              Volver al inicio de sesión
            </button>
          </>
        )}
      </div>

      <LegalLinks />
    </div>
  );
}

// Anonymous, like the header: the documents follow the hostname's brand. The
// paths come from the registry so a brand with its own routes cannot drift.
function LegalLinks(): React.JSX.Element {
  const brand = brandFromLocation();
  return (
    <nav className="mt-6 flex gap-4 text-[12px] text-ink-400">
      <a href={brand.termsPath} className="hover:text-ink-700 transition-colors">
        Términos y condiciones
      </a>
      <a href={brand.privacyNoticePath} className="hover:text-ink-700 transition-colors">
        Aviso de privacidad
      </a>
    </nav>
  );
}
